@import '../../../../constants/spacing.module.less';
@import '../../../../constants/z-index.module.less';
@import '../../../../app-brand/entities/entity-layer.module.less';

.combine-container {
  margin-bottom: @space-20;
  display: flex;
  justify-content: center;
  align-items: center;
}

.combine-button {
  background: var(--grey--200);
  border: 1px solid var(--grey--300);
  color: var(--grey--700);
  padding: @space-16 @space-24;
  border-radius: @space-8;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  text-align: center;

  &:hover {
    background: var(--grey--300);
    border-color: var(--grey--400);
  }

  &:active {
    background: var(--grey--400);
  }
}

.container.container {
  position: relative;
  gap: @space-8;
  padding: @space-16;
  border: 2px solid transparent;
  background:
    linear-gradient(var(--white), var(--white)) padding-box,
    linear-gradient(90deg, var(--blue-93CCFD) 0%, var(--purple-B085FF) 50%, var(--red-F1C1C1) 100%) border-box;
  border-radius: 8px;
}

.header-section {
  display: flex;
  align-items: flex-start;
  gap: @space-12;
  margin-bottom: @space-16;
}

.sparkle-icon {
  color: var(--orange-FFA500);
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: @space-4;
}

.header-content {
  flex: 1;
}

.title {
  color: var(--grey--900);
}

.description {
  color: var(--grey--600);
  font-size: 14px;
  margin: 0;
  font-weight: 600;
}

.input-section {
  margin-bottom: 0;
  margin-top: -@space-12;
}

.prompt-input {
  width: 100%;
}

.bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: @space-4;
}

.example-section {
  flex: 1;
}

.example-rule {
  color: var(--grey--600);
  font-size: 14px;
  font-weight: 400;
  margin: 0;
}

.bottom-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: @space-16;
}

.safety-section {
  flex: 1;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: @z-base-2;
  border-radius: 6px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: @space-12;
  text-align: center;
}

.loading-icon {
  width: 32px;
  height: 32px;
  color: var(--purple-8B5CF6);
  animation: sparkle-pulse 1.5s ease-in-out infinite;
}

.loading-dots {
  display: flex;
  gap: 6px;
}

.loading-dot {
  width: 8px;
  height: 8px;
  background-color: var(--purple-8B5CF6);
  border-radius: 50%;
  animation: loading-bounce 1.4s ease-in-out infinite;
  will-change: transform;

  &:nth-child(1) {
    animation-delay: 0s;
  }

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }
}

@keyframes sparkle-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
}

@keyframes loading-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

// Legacy styles - keeping for compatibility but may be unused now
.header-strip {
  border-bottom: 1px solid var(--grey--200);
  margin-bottom: @space-4;
}

.title-row {
  display: flex;
  align-items: center;
  gap: @space-8;
  margin-bottom: @space-4;
}

.title-sparkle-icon {
  color: var(--orange-FFA500);
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.instructional-text {
  color: var(--grey--700);
  font-size: 14px;
  font-weight: 400;
  margin-bottom: @space-8;
}

.input-row {
  display: flex;
  align-items: center;
  gap: @space-12;
}

.preview-button {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: @space-8;
  font-weight: 600;
  padding: @space-8 @space-16;
  border-radius: 6px;
  cursor: not-allowed;
  margin-right: @space-8;

  // Grayed out styling
  background: var(--grey--100);
  border: 2px solid var(--grey--200);
  color: var(--grey--400);
  opacity: 0.6;
}

.draft-button {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: @space-8;
  font-weight: 500;
  padding: @space-12 @space-20;
  border-radius: 6px;
  transition: all 0.2s ease;
  cursor: pointer;
  min-width: 120px;
  justify-content: center;

  // Default state - subtle styling
  background: var(--grey--100);
  border: 1px solid var(--grey--300);
  color: var(--grey--600);

  &:disabled {
    cursor: not-allowed;
    background: var(--grey--100);
    border: 1px solid var(--grey--300);
    color: var(--grey--500);
    opacity: 0.4;

    // Only apply disabled styling when NOT generating
    &.has-input:not(.generating) {
      background: var(--grey--100);
      border: 1px solid var(--grey--300);
      color: var(--grey--500);
      opacity: 0.7;
    }
  }

  // Colorful gradient when user starts typing
  &.has-input {
    background: var(--purple-8B5CF6);
    color: var(--white);
    opacity: 1;
    border: none;
    box-shadow: none;
  }

  // Same colorful animation when generating - higher specificity to override disabled
  &:disabled.generating {
    position: relative;
    background: var(--purple-8B5CF6);
    color: var(--white);
    opacity: 1;
    border: none;
    cursor: not-allowed;
    overflow: hidden;
    min-width: 120px;
    justify-content: center;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(251, 191, 36, 0.7),
        rgba(255, 215, 0, 0.9),
        rgba(251, 191, 36, 0.8),
        rgba(245, 158, 11, 0.6),
        transparent
      );
      animation: shimmer 2s ease-in-out infinite;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        45deg,
        var(--purple-8B5CF6),
        var(--purple-A855F7),
        var(--purple-C084FC),
        var(--orange-FBBF24),
        var(--orange-F59E0B),
        var(--purple-8B5CF6)
      );
      background-size: 400% 400%;
      animation: gradientShift 3s ease-in-out infinite;
      z-index: -1;
    }
  }
}

.error {
  margin-top: @space-12;
  color: var(--red--600);
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  25% {
    background-position: 100% 50%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
